"use client";
import BodylineText from "@/components/common/text/bodyline-text";
import HeadlineText from "@/components/common/text/headline-text";
import ScreenLayout from "@/components/layout/screen-layout";
import { BornReadyAIEraData } from "@/lib/dto/ai-engineering-squad/born-ready-ai-era-data";
import { WorkflowTabsData } from "@/lib/dto/ai-engineering-squad/workflow-tabs-data";
import { IconPrimaryCheck, ImgAiDesignWorkflow, ImgAiEngineeringWorkflow, ImgAiManagementWorkflow } from "@/lib/images";
import Image from "next/image";
import { useEffect, useState } from "react";

function BornReadyAIEraSection() {
    const [activeTab, setActiveTab] = useState("engineering");

    // Auto-animate tabs every 3 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            setActiveTab((prevTab) => {
                if (prevTab === "engineering") return "design";
                if (prevTab === "design") return "management";
                return "engineering";
            });
        }, 3000);

        return () => clearInterval(interval);
    }, []);

    return (
        <ScreenLayout outerClassName="bg-[#F8FCFF]">
            <section className="pt-8 pb-16">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col items-center space-y-6">

                        {/* Title */}
                        <HeadlineText
                            wrapperClassName="pt-0"
                            textClassName="text-gray-900"
                        >
                            {BornReadyAIEraData.title}
                        </HeadlineText>

                        {/* Description */}
                        <BodylineText
                            wrapperClassName="max-w-2xl mx-auto"
                            textClassName="text-gray-600"
                        >
                            {BornReadyAIEraData.description}
                        </BodylineText>

                        {/* Workflow Tabs Section */}
                        <div className="w-full mt-12">
                            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">

                                {/* Left Side - Image (35%) */}
                                <div className="w-full lg:w-2/6">
                                    <div className="relative h-64 lg:h-full rounded-2xl overflow-hidden">
                                        <Image
                                            src={
                                                activeTab === "engineering"
                                                    ? ImgAiEngineeringWorkflow
                                                    : activeTab === "design"
                                                        ? ImgAiDesignWorkflow
                                                        : activeTab === "management"
                                                            ? ImgAiManagementWorkflow
                                                            : ImgAiEngineeringWorkflow
                                            }
                                            alt={`AI ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Workflow`}
                                            fill
                                            className="object-cover transition-all duration-500 ease-in-out"
                                        />
                                    </div>
                                </div>

                                {/* Right Side - Tabs Container (65%) */}
                                <div className="w-full lg:w-3/5">
                                    <div className="bg-white py-2 overflow-hidden">

                                        {/* Tab Headers */}
                                        <div className="flex mx-4 border-b border-gray-200">
                                            {WorkflowTabsData.tabs.map((tab) => (
                                                <button
                                                    key={tab.id}
                                                    onClick={() => setActiveTab(tab.id)}
                                                    className={`flex-1 px-6 py-4 text-sm font-medium transition-all duration-300 ease-in-out ${activeTab === tab.id
                                                        ? "text-black border-b-2 border-black transform scale-105"
                                                        : "text-gray-400 hover:text-gray-900 hover:scale-102"
                                                        }`}
                                                >
                                                    {tab.name}
                                                </button>
                                            ))}
                                        </div>

                                        {/* Tab Content */}
                                        <div className="p-6 lg:p-8">
                                            {WorkflowTabsData.tabs.map((tab) => (
                                                <div
                                                    key={tab.id}
                                                    className={`space-y-4 transition-all duration-500 ease-in-out ${activeTab === tab.id
                                                        ? "opacity-100 transform translate-x-0"
                                                        : "opacity-0 transform translate-x-4 absolute"
                                                        }`}
                                                >
                                                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                                                        {tab.title}
                                                    </h3>
                                                    <ul className="space-y-3">
                                                        {tab.content.map((item, index) => (
                                                            <li key={index} className="flex items-start space-x-3">
                                                                <div className="flex-shrink-0 mt-1">
                                                                    <Image
                                                                        src={IconPrimaryCheck}
                                                                        alt="check icon"
                                                                        className="w-5 h-5"
                                                                    />
                                                                </div>
                                                                <span className="text-gray-700 leading-relaxed">
                                                                    {item}
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>

                                    </div>
                                </div>

                            </div>
                        </div>

                    </div>
                </div>
            </section>
        </ScreenLayout>
    );
}

export default BornReadyAIEraSection; 